import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../../auth/components/auth.service';
import { User } from '../../../../auth/shared/UserInterface';
import { ToastService } from '../../../../shared/services/toast.service';
import { ErrorHandlingService } from '../../../../shared/services/error-handling.service';
import { PaymentSectionComponent } from '../../../../shared/components/payment-section/payment-section.component';
import { CheckoutPaymentMethod } from '../../../../booking/booking-modal/payment/payment-options/payment-options.component';
import { VoucherCalculation } from '../../../../shared/components/payment-section/voucher-calculation.interface';
import { CourseSessionDto } from '../../../../service/court-availability-service';
import { CourseSession } from '../../../admin/models/course.interface';
import {
  CoursesService,
  PaymentMethod,
  EnrollmentIntentResponse,
  EnrollSessionRequest,
} from '../../services/courses.service';

// Type alias for session types that can be enrolled in
// CourseSessionDto (from aggregated slots API) has startTimeUtc/endTimeUtc
// CourseSession (from admin API) has startTime/endTime
export type EnrollableSession = CourseSessionDto | CourseSession;
import { OrganizationService } from '../../../../core/services/organization.service';
import { Router } from '@angular/router';
import { formatDateLongGerman } from '../../../../shared/utils/date-format.util';
import { to } from '../../../../core/constants/routes';
import { OrganizationSettingsService } from '../../../../shared/services/organization-settings.service';
import { PaypalOrderContext } from '../../../../booking/booking-modal/payment/paypal-checkout/paypal-checkout.component';
import { segmentEventLabel } from '../../../../booking/booking-container/court-grid-view/court-segment-label.util';

/**
 * CourseEnrollmentModalComponent - Modal for enrolling in course sessions
 *
 * This component handles:
 * 1. Displays session details
 * 2. Delegates payment/voucher handling to PaymentSectionComponent
 * 3. Creates enrollment intent for Stripe payments
 * 4. Confirms enrollment with the backend
 */
@Component({
  selector: 'app-course-enrollment-modal',
  standalone: true,
  imports: [
    CommonModule,
    PaymentSectionComponent,
    CurrencyPipe,
  ],
  templateUrl: './course-enrollment-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./course-enrollment-modal.component.css'],
})
export class CourseEnrollmentModalComponent implements OnInit, OnDestroy {
  @Input() session!: EnrollableSession;
  @Output() enrolled = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  // Payment state (managed by PaymentSectionComponent, we track for submit logic)
  /**
   * The method payment-options reported, or null until it has reported one
   * (e.g. while the SEPA mandate is still loading). Null keeps submit disabled
   * for a paid session — it must never be read as "cash".
   *
   * This and the other payment-step state the template reads (the Stripe
   * confirm state, `providerOwnsSubmit`, the intent loading/error state) are
   * SIGNALS because payment-options emits from its own lifecycle hooks
   * (ngOnInit / ngOnChanges), i.e. while this component's template is being
   * checked. A plain field changed at that point trips NG0100 on every binding
   * that reads it; a signal write marks the view for a fresh pass instead.
   */
  paymentMethod = signal<CheckoutPaymentMethod | null>(null);
  stripePaymentIntentId: string | null = null;
  stripeClientSecret: string | null = null;
  selectedVoucherCodes: string[] = [];
  voucherCalculation: VoucherCalculation | null = null;
  isFullyCovered = false;

  // Stripe Payment Element state
  private stripeConfirmFn = signal<(() => Promise<void>) | null>(null);
  stripeFormComplete = signal(false);

  // Enrollment intent state (for Stripe payments - course-specific). The
  // intent is requested from onPaymentMethodChanged('card'), which can run
  // during this component's check — hence signals for what the template reads.
  enrollmentIntent: EnrollmentIntentResponse | null = null;
  intentLoading = signal(false);
  intentError = signal<string | null>(null);

  // Processing state
  isProcessing = false;
  processingError: string | null = null;

  // Organization settings (for payment toggles)
  /**
   * "May THIS member pay THIS session at the venue?" — the RESOLVED, per-user,
   * per-session verdict from `GET /sessions/:id`, NOT the org-wide
   * `payment.onSitePaymentEnabled` this used to read.
   *
   * `course_sessions.on_site_payment` overrides the organization in BOTH
   * directions, and the org-wide public read cannot see it, so driving this
   * from the org flag was wrong twice over:
   * - WIDENING (org off, session `'all'`): the card was never rendered, so the
   *   member saw "Keine Zahlungsmethode verfügbar" and a dead submit button on
   *   a session an admin had explicitly opened.
   * - NARROWING (org on, session `'off'`): the card was offered and selected,
   *   and the member collected a 400 after committing.
   *
   * `false` until {@link onSitePaymentState} is `'ready'` — see there for why
   * the default must never be shown as an answer.
   */
  onSitePaymentEnabled = false;

  /**
   * Whether {@link onSitePaymentEnabled} is KNOWN yet.
   *
   * Three states rather than a boolean, because "not resolved" must not render
   * as "not available": while the fetch is in flight the payment options are
   * replaced by a spinner, and a FAILED fetch shows a message and blocks
   * submit rather than falling back to the organization-wide flag — that
   * fallback is precisely the bug this field exists to close.
   */
  onSitePaymentState: 'loading' | 'ready' | 'error' = 'loading';

  /** German copy for the failed-resolve state; rendered, not logged. */
  sessionLoadError: string | null = null;
  stripePaymentEnabled = false;
  sepaPaymentEnabled = false;
  voucherPaymentEnabled = false;
  paypalPaymentEnabled = false;
  paypalClientId: string | null = null;
  paypalEnvironment: 'sandbox' | 'live' | null = null;
  /**
   * PayPal renders its own submit button, so this modal's CTA must step aside
   * while PayPal is selected — see PaymentSectionComponent.
   */
  providerOwnsSubmit = signal(false);
  /** A voucher deduction is being validated — see PaymentSectionComponent.voucherCalculationPendingChanged. */
  voucherCalculationPending = signal(false);

  // Step state for showing success screen
  step = signal<'enrollment' | 'success'>('enrollment');

  /**
   * A full session turns an enrolment request into a WAITLIST entry
   * (`enrollInSession` answers `status: 'waitlisted'`, backend
   * `courses.service.ts`). That member holds no seat and owes nothing yet — no
   * booking is written at all — so every "Erfolgreich angemeldet" line, toast
   * and success heading alike, would be wrong twice over. These two carry the
   * server's verdict into the success step.
   */
  waitlisted = signal(false);
  waitlistPosition = signal<number | null>(null);

  private subscriptions: Subscription = new Subscription();
  /**
   * The in-flight enrollment-intent request. Only the latest may land: a
   * slower response for an older voucher selection would otherwise overwrite
   * the intent (and Stripe amount) of the newer one, or set its error.
   */
  private intentSub: Subscription | null = null;

  constructor(
    public authService: AuthService,
    private toastService: ToastService,
    private coursesService: CoursesService,
    public organizationService: OrganizationService,
    private router: Router,
    private organizationSettingsService: OrganizationSettingsService,
    private errorHandlingService: ErrorHandlingService
  ) {}

  ngOnInit(): void {
    // PaymentSectionComponent handles voucher loading

    // Load organization settings for the org-wide payment toggles.
    // On-site is DELIBERATELY not among them — see below.
    const orgId = this.getOrganizationId();
    if (orgId) {
      this.loadOrganizationSettings(orgId);
    }

    // A free session renders no payment options at all, so there is no
    // on-site card to decide about and nothing to resolve. Anything else has
    // to ask the server about THIS session and THIS member.
    if (this.isFreeSession()) {
      this.onSitePaymentState = 'ready';
      return;
    }
    this.loadOnSitePaymentAvailability();
  }

  /**
   * Resolve the on-site verdict for this session and this member.
   *
   * The browse list hands the modal a session it fetched WITHOUT a paying user
   * (the aggregated slot payload and `GET /courses/:id/sessions` are list
   * reads), so it cannot carry a per-user answer and deliberately does not.
   * The modal therefore owns the fetch — that is the whole point of it living
   * here rather than in the caller.
   */
  loadOnSitePaymentAvailability(): void {
    this.onSitePaymentState = 'loading';
    this.sessionLoadError = null;

    this.subscriptions.add(
      this.coursesService.getSession(this.session.id).subscribe({
        next: (detail) => {
          this.onSitePaymentEnabled = detail.onSitePaymentAvailable;
          this.onSitePaymentState = 'ready';
        },
        error: (err) => {
          // NO fallback to the organization-wide flag. Guessing here is the
          // defect: guess "on" and a session pinned to 'off' 400s after the
          // member commits; guess "off" and a session an admin opened becomes
          // unreachable. Refusing visibly is the honest answer.
          this.onSitePaymentEnabled = false;
          this.sessionLoadError = `Die Zahlungsoptionen für diesen Termin konnten nicht geladen werden. ${this.errorHandlingService.getGeneralErrorMessage(err)}`;
          this.onSitePaymentState = 'error';
          this.toastService.error(this.sessionLoadError);
        },
      })
    );
  }

  private loadOrganizationSettings(organizationId: string): void {
    this.subscriptions.add(
      this.organizationSettingsService.getPublicSettings(organizationId).subscribe((settings) => {
        // `settings.payment.onSitePaymentEnabled` is NOT read here, on purpose:
        // it is the organization's answer, and a course session may override it
        // in both directions. See `onSitePaymentEnabled` / the fetch above.
        this.stripePaymentEnabled = settings.payment.stripePaymentEnabled;
        this.voucherPaymentEnabled = settings.payment.voucherPaymentEnabled;
        this.sepaPaymentEnabled = settings.payment.sepaPaymentEnabled;
        this.paypalPaymentEnabled = settings.payment.paypalPaymentEnabled;
        this.paypalClientId = settings.payment.paypalClientId;
        this.paypalEnvironment = settings.payment.paypalEnvironment;
      })
    );
  }

  ngOnDestroy(): void {
    this.intentSub?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  getCurrentUser(): User | null | undefined {
    return this.authService.currentUserSignal();
  }

  onCancel(): void {
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey(): void {
    if (this.step() === 'success') {
      this.closeModal();
    } else {
      this.onCancel();
    }
  }

  // ============================================================================
  // Payment Section Event Handlers
  // ============================================================================

  onPaymentMethodChanged(method: CheckoutPaymentMethod | null): void {
    this.paymentMethod.set(method);
    // Reset Stripe state so confirm button doesn't stay enabled with stale callback
    this.stripeConfirmFn.set(null);
    this.stripeFormComplete.set(false);

    // Create enrollment intent when card is selected (to get Stripe client secret)
    // Only if Stripe is actually enabled. Not while a voucher tick is being
    // validated: `selectedVoucherCodes` already holds the unchecked code. The
    // calculation always reports its result (accepted, rolled back or
    // cleared) through onVoucherCalculationChanged, which creates the intent
    // from the settled codes.
    if (
      method === 'card' &&
      !this.enrollmentIntent &&
      this.stripePaymentEnabled &&
      !this.voucherCalculationPending()
    ) {
      this.createEnrollmentIntent(PaymentMethod.STRIPE);
    }
  }

  onPaymentSuccess(event: { paymentIntentId: string; bookingId?: string }): void {
    // PayPal's capture ALREADY enrolled the user server-side, in the same
    // transaction as the capture record. Calling enrollInSession here would hit
    // "Already enrolled" at best, and double-book at worst.
    if (this.paymentMethod() === 'paypal') {
      this.isProcessing = false;
      this.step.set('success');
      this.toastService.success('Erfolgreich angemeldet! Zahlung erfolgreich.');
      return;
    }

    // Decide on the voucher flag, not on the SHAPE of the id. The old
    // `startsWith('pi_')` test silently classified any non-Stripe provider id as
    // "vouchers covered it" — a completed payment would have been enrolled as
    // if it were free.
    if (this.isFullyCovered || !event.paymentIntentId) {
      this.completeEnrollment();
      return;
    }
    this.stripePaymentIntentId = event.paymentIntentId;
    this.completeEnrollment();
  }

  onPaymentError(event: { error: string }): void {
    this.processingError = event.error;
    this.isProcessing = false;
  }

  onProviderOwnsSubmit(owns: boolean): void {
    this.providerOwnsSubmit.set(owns);
  }

  /**
   * Whether PayPal currently owns the submit, i.e. whether our CTA must step
   * aside. Only while PayPal's own button is actually on screen: once vouchers
   * cover the whole amount, payment-section unmounts payment-options — PayPal
   * button included — without any further emission, so the last
   * `providerOwnsSubmit(true)` would otherwise leave the member with no submit
   * control at all. Unticking a voucher remounts payment-options, which
   * re-emits its selection from scratch.
   */
  isSubmitOwnedByProvider(): boolean {
    return this.providerOwnsSubmit() && !this.isFullyCoveredByVouchers();
  }

  /**
   * What PayPal is being asked to pay for: this course session.
   *
   * `organizationId` is part of the context because the course order route is
   * organization-scoped like every other member course route (§6.3.2b) — the
   * server rejects the order without it, so the button must not render an
   * order it cannot create.
   */
  get paypalOrderContext(): PaypalOrderContext | undefined {
    const organizationId = this.getOrganizationId();
    return this.session?.id && organizationId
      ? { kind: 'course', sessionId: this.session.id, organizationId }
      : undefined;
  }

  onConfirmFnReady(fn: () => Promise<void>): void {
    this.stripeConfirmFn.set(fn);
  }

  onPaymentFormComplete(complete: boolean): void {
    this.stripeFormComplete.set(complete);
  }

  onVoucherCalculationChanged(calculation: VoucherCalculation | null): void {
    this.voucherCalculation = calculation;

    // Clear existing enrollment intent when vouchers change (Stripe needs to be re-created).
    // A request still in flight was built for the previous codes — drop it too.
    this.intentSub?.unsubscribe();
    this.intentSub = null;
    this.intentLoading.set(false);
    this.intentError.set(null);
    this.enrollmentIntent = null;
    this.stripeClientSecret = null;
    this.stripePaymentIntentId = null;

    // Re-create enrollment intent if card payment is selected
    // This ensures Stripe shows the correct remaining amount after voucher deduction
    // Only if Stripe is actually enabled
    if (this.paymentMethod() === 'card' && this.stripePaymentEnabled) {
      this.createEnrollmentIntent(PaymentMethod.STRIPE);
    }
  }

  onFullyCoveredByVouchersChanged(isCovered: boolean): void {
    this.isFullyCovered = isCovered;
  }

  onSelectedVoucherCodesChanged(codes: string[]): void {
    this.selectedVoucherCodes = codes;
  }

  // ============================================================================
  // Helper Methods for Template
  // ============================================================================

  /**
   * Type guard: checks if session is from aggregated slots API (has startTimeUtc)
   */
  private isAggregatedSession(session: EnrollableSession): session is CourseSessionDto {
    return 'startTimeUtc' in session;
  }

  /**
   * Gets the session start time, handling both CourseSessionDto (startTimeUtc) and CourseSession (startTime) formats
   */
  getSessionStartTime(): string {
    return this.isAggregatedSession(this.session)
      ? this.session.startTimeUtc
      : this.session.startTime;
  }

  /**
   * Gets the session end time, handling both CourseSessionDto (endTimeUtc) and CourseSession (endTime) formats
   */
  getSessionEndTime(): string {
    return this.isAggregatedSession(this.session)
      ? this.session.endTimeUtc
      : this.session.endTime;
  }

  /**
   * Gets the session price, handling both CourseSessionDto (priceForUser) and CourseSession (pricePerSession) formats
   */
  getSessionPrice(): number {
    if (this.isAggregatedSession(this.session)) {
      return this.session.priceForUser ?? this.session.regularPrice ?? 0;
    }
    return this.session.pricePerSession ?? 0;
  }

  getOrganizationId(): string | undefined {
    return this.organizationService.selectedOrganizationId() || undefined;
  }

  /**
   * Label for the thing being joined, from the ONE §6.4.3 label table.
   * Both shapes behind `EnrollableSession` — the aggregated-grid payload
   * (`CourseSessionDto`) and the admin payload (`CourseSession`) — declare
   * `kind` and `eventType`, so both are read directly.
   *
   * `kind` is passed through rather than hardcoded to 'course': a block is
   * never joinable and never reaches this modal, but if one ever did the label
   * table must fail closed to "Gesperrt" instead of the most misleading word
   * available ("Kurs") — which would also land in the payment description.
   */
  get kindLabel(): string {
    return segmentEventLabel(this.session?.kind, this.session?.eventType);
  }

  /**
   * Also the PayPal order description (`paypal-checkout.service.ts`), so it
   * must name the SESSION (§6.4.2) — a member paying for "vs. TC Musterstadt"
   * must not find "Medenspiele Herren 40" on their statement.
   */
  getPaymentDescription(): string {
    return `${this.kindLabel}: ${this.session.displayName} am ${this.formatDate(this.getSessionStartTime())}`;
  }

  isFullyCoveredByVouchers(): boolean {
    return this.isFullyCovered;
  }

  /**
   * A session that costs nothing. There is no payment method to pick, no
   * voucher to apply and no amount to show, so the payment section and the
   * price figure are both replaced by a plain "Kostenlos".
   */
  isFreeSession(): boolean {
    return this.getSessionPrice() <= 0;
  }

  /**
   * Determines if the confirm button should be disabled.
   * Button is disabled when:
   * - Enrollment is processing
   * - Intent is loading
   * - No payment method reported yet (e.g. SEPA mandate still loading)
   * - Card payment selected but form not complete or confirm not ready
   * - No payment methods available AND vouchers don't fully cover the price
   */
  isConfirmButtonDisabled(): boolean {
    // Always disabled during processing or loading
    if (this.isProcessing || this.intentLoading()) {
      return true;
    }

    // A voucher tick is still being validated: submitting now would send the
    // unchecked code. Checked before the full-cover shortcut, because the
    // pending result can change whether vouchers cover the price at all.
    if (this.voucherCalculationPending()) {
      return true;
    }

    // Nothing is owed, so no payment method is needed — the "no method
    // available" guard below must not block a free enrollment.
    if (this.isFreeSession()) {
      return false;
    }

    // The per-session on-site verdict is not in yet, or could not be fetched.
    // Submitting now would submit against a guess, which is exactly what this
    // whole change removes. The template says why in both states.
    if (this.onSitePaymentState !== 'ready') {
      return true;
    }

    // If fully covered by vouchers, always allow submission
    if (this.isFullyCoveredByVouchers()) {
      return false;
    }

    // Payment-options has not decided on a method yet — there is nothing to
    // submit with.
    const method = this.paymentMethod();
    if (method === null) {
      return true;
    }

    // If card payment selected but form not complete or confirm not ready
    if (method === 'card' && (!this.stripeFormComplete() || !this.stripeConfirmFn())) {
      return true;
    }

    // If no payment methods available (voucher-only mode) but vouchers don't cover full amount.
    // SEPA is only selectable with a usable mandate (guarded in payment-options), so a 'sepa'
    // selection means a valid method exists even when on-site/Stripe are off.
    if (
      !this.onSitePaymentEnabled &&
      !this.stripePaymentEnabled &&
      method !== 'sepa'
    ) {
      return true;
    }

    return false;
  }

  // ============================================================================
  // Enrollment Intent (Course-specific - Stripe requires pre-created intent)
  // ============================================================================

  private createEnrollmentIntent(paymentMethod: PaymentMethod): void {
    this.intentLoading.set(true);
    this.intentError.set(null);

    this.intentSub?.unsubscribe();
    this.intentSub =
      this.coursesService
        .createEnrollmentIntent(this.session.id, {
          paymentMethod,
          voucherCodes:
            this.selectedVoucherCodes.length > 0
              ? this.selectedVoucherCodes
              : undefined,
        })
        .subscribe({
          next: (response) => {
            this.enrollmentIntent = response;
            this.stripeClientSecret =
              response.paymentIntentClientSecret || null;
            this.stripePaymentIntentId =
              response.stripePaymentIntentId || null;
            this.intentLoading.set(false);

            // Check if session is full
            if (response.isFull) {
              this.intentError.set('Diese Session ist leider bereits voll.');
            }
          },
          error: (err) => {
            this.intentError.set(
              this.errorHandlingService.getGeneralErrorMessage(err)
            );
            this.intentLoading.set(false);
          },
        });
  }

  // ============================================================================
  // Form Submission
  // ============================================================================

  async onSubmit(): Promise<void> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      this.processingError = 'Du musst angemeldet sein, um teilzunehmen.';
      return;
    }

    // Nothing is owed: no payment section is rendered, so no method is ever
    // reported — enrol without one rather than waiting for a choice that
    // cannot come.
    if (this.isFreeSession()) {
      this.completeEnrollment();
      return;
    }

    // Handle voucher-only payments
    if (this.isFullyCoveredByVouchers()) {
      this.completeEnrollment();
      return;
    }

    const method = this.paymentMethod();

    // Handle card payment: trigger Stripe confirm, then enrollment completes via onPaymentSuccess
    if (method === 'card') {
      const confirmFn = this.stripeConfirmFn();
      if (!confirmFn) {
        this.processingError = 'Zahlungsformular nicht bereit';
        return;
      }
      this.isProcessing = true;
      this.processingError = null;
      await confirmFn();
      // Result flows back via onPaymentSuccess() or onPaymentError()
      return;
    }

    // Handle SEPA direct debit: confirmed on trust, collected later in a batch (no Stripe confirm)
    if (method === 'sepa') {
      this.completeEnrollment();
      return;
    }

    // Handle cash/venue payment. null (no method decided yet) and 'paypal'
    // (enrolled by the PayPal capture) submit nothing here.
    if (method === 'cash') {
      this.completeEnrollment();
    }
  }

  private completeEnrollment(): void {
    this.isProcessing = true;
    this.processingError = null;

    const method = this.paymentMethod();
    const request: EnrollSessionRequest = {
      // null only reaches here for a free or fully voucher-covered enrolment
      // (see onSubmit), where nothing is collected; the backend settles those
      // regardless of the named method, as it did when this defaulted to cash.
      paymentMethod:
        method === 'card'
          ? PaymentMethod.STRIPE
          : method === 'sepa'
            ? PaymentMethod.SEPA_DEBIT
            : PaymentMethod.ON_SITE,
      // Only send the Stripe intent for card payments — a stale intent id must not
      // ride along on a SEPA/on-site enrollment.
      stripePaymentIntentId:
        method === 'card'
          ? this.stripePaymentIntentId || undefined
          : undefined,
      voucherCodes:
        this.selectedVoucherCodes.length > 0
          ? this.selectedVoucherCodes
          : undefined,
    };

    this.subscriptions.add(
      this.coursesService.enrollInSession(this.session.id, request).subscribe({
        next: (response) => {
          this.isProcessing = false;

          // Show appropriate success message.
          // `paymentRequired` is the server's word on whether anything is owed
          // (it is false for a 0,00 € session). A waitlist entry also reports
          // `paymentRequired: false`, so the status check keeps it out of the
          // free branch.
          //
          // The waitlist branch comes FIRST and is the only one that may run
          // for `status !== 'enrolled'`. Without it a waitlisted member fell
          // through every `enrolled`/voucher/card/sepa test to the final else
          // and was told "Bitte vor Ort bezahlen." — on a free session too,
          // because the suppressed payment section never reports a method
          // (it used to default to 'cash'). They hold no seat and owe nothing.
          if (response.status === 'waitlisted') {
            this.waitlisted.set(true);
            this.waitlistPosition.set(response.waitlistPosition ?? null);
            const position = response.waitlistPosition;
            this.toastService.info(
              position
                ? `Diese Session ist ausgebucht – Sie stehen auf Platz ${position} der Warteliste. Es ist noch nichts zu bezahlen.`
                : 'Diese Session ist ausgebucht – Sie stehen jetzt auf der Warteliste. Es ist noch nichts zu bezahlen.'
            );
          } else if (response.status === 'enrolled' && !response.paymentRequired) {
            this.toastService.success(
              'Erfolgreich angemeldet! Diese Anmeldung ist kostenlos.'
            );
          } else if (this.isFullyCoveredByVouchers()) {
            this.toastService.success(
              'Erfolgreich angemeldet! (Bezahlt mit Gutschein)'
            );
          } else if (method === 'card') {
            this.toastService.success(
              'Erfolgreich angemeldet! Zahlung erfolgreich.'
            );
          } else if (method === 'sepa') {
            this.toastService.success(
              'Erfolgreich angemeldet! Der Betrag wird per SEPA-Lastschrift eingezogen.'
            );
          } else {
            this.toastService.success(
              'Erfolgreich angemeldet! Bitte vor Ort bezahlen.'
            );
          }

          this.step.set('success');
          // Don't emit enrolled here - wait for user to close the success screen
          // This prevents the parent from closing the modal before showing success
        },
        error: (err) => {
          this.isProcessing = false;
          this.processingError =
            this.errorHandlingService.getGeneralErrorMessage(err);
          this.toastService.error(this.processingError);
        },
      })
    );
  }

  // ============================================================================
  // Formatting Helper Methods
  // ============================================================================

  formatTime(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatDate(isoString: string): string {
    return formatDateLongGerman(isoString);
  }

  getCourtNames(session: EnrollableSession): string {
    return session.courts?.map(c => c.courtName).join(', ') || '';
  }

  /**
   * Gets the availability level for color coding
   * - 'plenty': > 50% spots available (green)
   * - 'limited': 20-50% spots available (orange)
   * - 'scarce': < 20% OR <= 3 spots (red)
   */
  getAvailabilityLevel(): 'plenty' | 'limited' | 'scarce' {
    if (this.session.maxParticipants === 0) return 'scarce';
    const percentAvailable = (this.session.availableSpots / this.session.maxParticipants) * 100;
    if (this.session.availableSpots <= 3 || percentAvailable < 20) return 'scarce';
    if (percentAvailable < 50) return 'limited';
    return 'plenty';
  }

  /**
   * Gets the fill percentage for the progress bar (how full the course is)
   */
  getFillPercentage(): number {
    if (this.session.maxParticipants === 0) return 100;
    return ((this.session.maxParticipants - this.session.availableSpots) / this.session.maxParticipants) * 100;
  }

  // ============================================================================
  // Success Screen Actions
  // ============================================================================

  goToMyCourses(): void {
    this.enrolled.emit();
    this.closed.emit();
    this.router.navigate(to.mySessions());
  }

  closeModal(): void {
    this.enrolled.emit();
    this.closed.emit();
  }

  onBackdropClick(): void {
    if (this.step() === 'success') {
      this.closeModal();
    } else {
      this.onCancel();
    }
  }
}
